import React from "react";

import FilterBar from "shared/components/FilterBar";
import Icon from "shared/components/Icon";
import Table from "shared/components/Table";
import { RegularText } from "shared/components/Typography";

import {
  COSIGN_DASHBOARD_ACTION_LABELS,
  CosignUnmappedTableProps,
  formatCosignSectionTitle,
} from "./cosignDashboard.config";
import { TableHeader, TableSection, TableWrapper } from "./styled";

/**
 * Section 2 table — VERO properties not yet mapped to Cosign (PRODUCT-988).
 */
const CosignUnmappedPropertiesTable: React.FC<CosignUnmappedTableProps> = ({
  properties,
  totalUnmappedCount,
  isLoading,
  searchTerm,
  onSearchChange,
  onMapToExisting,
  onCreateInCosign,
}) => {
  const tableFilters = {
    title: "",
    search: {
      action: (value: string) => onSearchChange(value),
      placeholder: "Search by name or address",
      width: 320,
      debounce: true,
      disabled: false,
      value: searchTerm,
    },
    clearFilters: searchTerm
      ? {
          onClick: () => onSearchChange(""),
          order: 2,
        }
      : undefined,
  };

  return (
    <TableSection data-testid="cosign-unmapped-section">
      <TableHeader>
        <h2>
          {formatCosignSectionTitle(
            "Other VERO Properties",
            properties.length,
            totalUnmappedCount
          )}
        </h2>
      </TableHeader>
      <TableWrapper>
        <FilterBar {...tableFilters} />
        <Table
          alt
          searching={Boolean(searchTerm)}
          noClickableRows
          loading={isLoading}
          dropdownColumnTitle="Actions"
          columns={[
            { title: "Property", dataIndex: "name", key: "name" },
            { title: "Address", dataIndex: "fullAddress", key: "fullAddress" },
            { title: "Units", dataIndex: "numberOfUnits", key: "numberOfUnits" },
          ]}
          rows={properties.map((property) => ({
            key: String(property.veroPropertyId),
            values: [
              <RegularText key={`${property.veroPropertyId}-name`} strong>
                {property.name}
              </RegularText>,
              <RegularText key={`${property.veroPropertyId}-fullAddress`}>
                {property.fullAddress}
              </RegularText>,
              <RegularText key={`${property.veroPropertyId}-numberOfUnits`}>
                {property.numberOfUnits}
              </RegularText>,
            ],
            dropdownItems: [
              {
                key: `cosign-map-existing-${property.veroPropertyId}`,
                label: COSIGN_DASHBOARD_ACTION_LABELS.mapToExisting,
                onClick: () => onMapToExisting(property),
              },
              {
                key: `cosign-create-${property.veroPropertyId}`,
                label: COSIGN_DASHBOARD_ACTION_LABELS.createInCosign,
                onClick: () => onCreateInCosign(property),
              },
            ],
          }))}
          columnBreakpoints={{
            xs: {
              column1: "name",
              column2: "fullAddress",
            },
            sm: ["name", "fullAddress"],
            md: ["name", "fullAddress", "numberOfUnits"],
            lg: ["name", "fullAddress", "numberOfUnits"],
          }}
          placeholder={{
            Icon: Icon.NoDataIcon,
            label: searchTerm
              ? "No properties match your search"
              : "All VERO properties are mapped to Cosign",
          }}
        />
      </TableWrapper>
    </TableSection>
  );
};

export default CosignUnmappedPropertiesTable;
